import type { UseQueryOverrideOptions, UIAlertProps, MoneyLimitsComponents } from '@core/types'
import type { LimitValue, LimitTypeUnion, ValidationErrors, Limits, MutatingLimits, MutatingLimit, LimitStatuses, Config, Limit } from '@payments/types'
import { Requirement, PromptType, LimitType, Period, InputType, SetBy } from '@payments/types'
import { LimitType as ClientLimitType } from '@core/types/clients/money-limits'
import { useTimeoutFn } from '@vueuse/core'
import { ref, shallowRef, computed, watch, useMoneyLimitsClient, isRefOrGetter, toValue, useState, useDayjs, useQuery, useNuxtData, useCookie, useFormat, firstToUpper, useCoreConfig, usePaymentsConfig, useI18n, useRoute, navigateTo, OpenapiError, useUserInfo } from '#imports'

const VERSION_EVEN = '333_1_3'
const VERSION_ODD = '333_4_3'

// Exhaustive by LimitTypeUnion: the module enum is the app-facing domain, the generated enum
// is what the client's `{limitType}` path parameter accepts.
const LIMIT_TYPE_TO_CLIENT: Record<LimitTypeUnion, ClientLimitType> = {
    [LimitType.DEPOSIT]: ClientLimitType.depositLimits,
    [LimitType.LOSS]: ClientLimitType.lossLimits,
}

export const useMoneyLimits = () => {
    const { site } = useCoreConfig()
    const { client } = useMoneyLimitsClient()
    const { t } = useI18n()
    const { data: user } = useUserInfo()
    const { asMoney, asRelativeTime } = useFormat()

    const { deposit } = usePaymentsConfig()
    // The A/B test only runs on AcFinten (UK) sites; elsewhere events stay unversioned and the original flow shows
    const version = computed(() => {
        if (!deposit.useAcFinten) return undefined

        return Number(user.value?.id) % 2 === 0 ? VERSION_EVEN : VERSION_ODD
    })
    const isVariantB = computed(() => version.value === VERSION_ODD)
    const route = useRoute()
    const dayjs = useDayjs()
    const hasAcceptedLimitsAfterReactivation = useCookie<string | undefined>('hasAcceptedLimitsAfterReactivation', {
        sameSite: 'lax',
        maxAge: dayjs.duration({ years: 1 }).asSeconds(),
        path: '/',
        secure: true,
    })

    const createDefaultMutatingLimits = (): MutatingLimits => ({
        [Period.DAILY]: {
            active: false,
            period: Period.DAILY,
            value: null,
        },
        [Period.WEEKLY]: {
            active: false,
            period: Period.WEEKLY,
            value: null,
        },
        [Period.MONTHLY]: {
            active: false,
            period: Period.MONTHLY,
            value: null,
        },
    })

    const queryLimitTypes: Record<string, LimitTypeUnion> = {
        deposit: LimitType.DEPOSIT,
        loss: LimitType.LOSS,
    }

    const selectedLimitType = useState<LimitTypeUnion | undefined>('moneyLimits:selectedLimitType')
    const wantsToPickLimitType = useState<boolean>('moneyLimits:wantsToPickLimitType', () => false)

    const limitTypeFromQuery = queryLimitTypes[String(route.query?.limitType)]

    // Only the query param overrides an existing pick — useMoneyLimits runs in several
    // components, and an unconditional assignment would wipe the user's dialog choice.
    // wantsToPickLimitType wins over the query: after the user presses back to the type
    // picker, components mounting (TypeSelect) must not re-apply the query's type.
    if (limitTypeFromQuery && !wantsToPickLimitType.value) selectedLimitType.value = limitTypeFromQuery

    const { data: configs } = useNuxtData<Partial<Record<LimitTypeUnion, Config>>>('moneyLimits:configs')
    const { data: sliderOptions } = useNuxtData('moneyLimits:slider-options')
    // Keyed by limit type: a site can have both deposit and loss limits enabled, and the
    // deposit flow needs the deposit-limit data even while the user is looking at loss limits.
    const { data: combinedMoneyLimits } = useNuxtData<Partial<Record<LimitTypeUnion, LimitStatuses>>>('moneyLimits:limits')

    const adjustLimitsAfterReactivation = useState<boolean | undefined>('moneyLimits:adjustLimitsAfterReactivation', () => false)
    const inputType = useState<InputType.SLIDERS | InputType.INPUTS | undefined>('moneyLimits:inputType')
    const mutatingLimits = useState<MutatingLimits>('moneyLimits:mutatingLimits', createDefaultMutatingLimits)
    const alert = useState<UIAlertProps | undefined>('moneyLimits:alert')
    const showDialog = useState<boolean>('moneyLimits:showDialog', () => false)
    const isFromManualLink = useState<boolean>('moneyLimits:-is-from-manual-link', () => false)
    const wantsToEditLimits = useState<boolean>('moneyLimits:wantsToEditLimits', () => false)
    const isPickingLimitType = useState<boolean>('moneyLimits:isPickingLimitType', () => false)
    const isExplanationPassed = useState<boolean>('moneyLimits:isExplanationPassed', () => false)
    const isLoading = useState<boolean>('moneyLimits:is-loading', () => true)
    const validationErrors = useState<ValidationErrors>('moneyLimits:validationErrors', () => ({
        [Period.DAILY]: null,
        [Period.WEEKLY]: null,
        [Period.MONTHLY]: null,
    }))
    const clampedValues = useState<Partial<Record<Period, number>>>('moneyLimits:clampedValues', () => ({}))
    // Shared: the views save through their own useMoneyLimits() call, while the dialog step reads it as busy
    const updating = useState<boolean>('moneyLimits:updating', () => false)
    const highRiskDepositLimitReached = ref<boolean>(false)

    // The cached response also carries a `highRisks` block, so entries without `enabled` are
    // not configs at all.
    const enabledConfigs = computed<Config[]>(() =>
        Object.values(configs.value ?? {}).filter((limitConfig): limitConfig is Config => !!limitConfig && 'enabled' in limitConfig && limitConfig.enabled)
    )

    // A type selection only means anything when there is something to choose between: with a
    // single enabled config the selection is ignored entirely, so config resolution, the
    // queries it drives and their timing stay identical to a site without limit-type support.
    const hasMultipleLimitTypes = computed(() => enabledConfigs.value.length > 1)

    const activeLimitType = computed(() => hasMultipleLimitTypes.value ? selectedLimitType.value : undefined)

    // Falls back to the first enabled config so the dialog can decide whether to open at all
    // before the user has picked a type; the pick then narrows it.
    const config = computed(() => enabledConfigs.value.find(limitConfig => limitConfig.type === activeLimitType.value) ?? enabledConfigs.value[0])

    // Not gated on showDialog: the money-limits page renders the app without the dialog
    // and needs the type selection too.
    const needsLimitTypeSelection = computed(() => {
        if (!hasMultipleLimitTypes.value) return false

        return !selectedLimitType.value
    })

    // Both types are fetched when both are enabled and set — the deposit flow needs the
    // deposit limits even while the money-limits UI is scoped to the loss limits.
    const limitTypesWithLimits = computed(() => enabledConfigs.value.filter(limitConfig => limitConfig.userHasAnyLimit).map(limitConfig => limitConfig.type))

    const hasLimitsToFetch = computed(() => limitTypesWithLimits.value.length > 0)

    const isReactivatedUser = computed(() =>
        user.value?.nthLogin === 1
        && hasLimitsToFetch.value
        && !adjustLimitsAfterReactivation.value
        && !hasAcceptedLimitsAfterReactivation.value
    )

    const type = computed(() => config.value?.type)
    const prompt = computed(() => config.value?.prompt)
    const requirement = computed(() => config.value?.requirement)
    // A limit of either enabled type satisfies the requirement — with deposit + loss both
    // enabled, a user holding only a loss limit must not be re-prompted on play.
    const requireMoneyLimits = computed(() =>
        enabledConfigs.value.length > 0 && enabledConfigs.value.every(limitConfig => limitConfig.userHasAnyLimit === false)
    )

    function limitsBySetBy(setBy: SetBy, limitType?: LimitTypeUnion): Partial<Limits> {
        if (!limitType) return {}

        return combinedMoneyLimits.value?.[limitType]?.limits?.reduce<Partial<Limits>>((acc, limit) => {
            if (limit.setBy !== setBy) return acc

            acc[limit.period] = limit

            return acc
        }, {}) ?? {}
    }

    const limits = computed<Partial<Limits>>(() => limitsBySetBy(SetBy.PLAYER, type.value))

    // The reactivated prompt lists every limit the user brought along, not just the
    // currently selected type's — a UK user can return with both deposit and loss limits.
    const reactivatedLimits = computed(() => limitTypesWithLimits.value.map(limitType => ({
        type: limitType,
        limits: Object.values(limitsBySetBy(SetBy.PLAYER, limitType)),
    })))

    const employeeLimits = computed<Partial<Limits>>(() => limitsBySetBy(SetBy.EMPLOYEE, type.value))

    const limitsLoaded = computed(() => !!combinedMoneyLimits.value?.[type.value as LimitTypeUnion] || !config.value?.userHasAnyLimit)

    const pendingLimits = computed<Limit[]>(() => Object.values(limits.value).filter((limit): limit is Limit => !!limit?.pendingValue))

    const hasPendingLimits = computed(() => pendingLimits.value.length > 0)

    const isSignupPrompt = computed(() => prompt.value === PromptType.SIGNUP)
    const isPlayPrompt = computed(() => prompt.value === PromptType.PLAY)
    const isDepositLimit = computed(() => type.value === LimitType.DEPOSIT)
    const isLossLimit = computed(() => type.value === LimitType.LOSS)

    const activeLimits = computed(() => Object.values(mutatingLimits.value).filter(limit => limit.active))
    const inactiveLimits = computed(() => Object.values(mutatingLimits.value).filter(limit => !limit.active))

    const isAboveOrEqualToMinAmount = computed(() => Object.values(mutatingLimits.value)
        .every(limit =>
            limit.value === null
            || limit.value === 'inf'
            || limit.value === ''
            || Number(limit.value) >= Number(sliderOptions.value?.[limit.period].min)
        )
    )

    const atLeastOneFilled = computed(() => Object.values(mutatingLimits.value).some(limit => Number(limit.value) >= Number(sliderOptions.value?.[limit.period].min)))

    const areLimitsUnderMax = computed(() => Object.values(mutatingLimits.value)
        .every(limit =>
            Number(limit.value) <= Number(sliderOptions.value?.[limit.period].max)
            || limit.value === 'inf'
            || limit.value === null
        )
    )

    const remainingLimit = computed(() => remainingLimits.value?.[0])

    const hasValidationError = computed(() => Object.values(validationErrors.value).some(validationError => validationError?.type === 'error'))

    // Same normalization as the updateLimits payload: empty/inactive means 'inf'
    const asEffectiveValue = (value: LimitValue) => value?.toString()?.length ? String(value) : 'inf'

    const hasChanges = computed(() => {
        const mutating = Object.values(mutatingLimits.value)

        // All-null means setCurrentLimits hasn't run yet; avoid a false positive that would submit inf for every limit
        if (mutating.every(limit => limit.value === null)) return false

        return mutating.some((limit) => {
            const baseline = limits.value?.[limit.period]

            // Pending values seed the inputs, so they are the baseline for "did the user change anything"
            return asEffectiveValue(limit.value) !== asEffectiveValue(baseline?.pendingValue ?? baseline?.value ?? null)
        })
    })

    function getRemainingLimits(limitType?: LimitTypeUnion) {
        const allLimits = [
            ...Object.values(limitsBySetBy(SetBy.PLAYER, limitType)),
            ...Object.values(limitsBySetBy(SetBy.EMPLOYEE, limitType))
        ]

        const lowestMap = allLimits.reduce((acc, limit) => {
            if (!limit) return acc
            if (limit.value === null) return acc
            if (limit.value === 'inf') return acc

            const period = limit.period
            const currentMin = acc[period]

            if (!currentMin || Number(limit.remaining) < Number(currentMin.remaining)) {
                acc[period] = limit
            }

            return acc
        }, {} as Partial<Record<Period, Limit>>)

        return Object.values(lowestMap).sort((a, b) => Number(a.remaining) - Number(b.remaining))
    }

    const remainingLimits = computed(() => getRemainingLimits(type.value))

    // The deposit flow must respect the deposit limit whichever type the money-limits UI
    // currently shows, so it reads the deposit type explicitly instead of the selected one.
    const isDepositLimitEnabled = computed(() => enabledConfigs.value.some(limitConfig => limitConfig.type === LimitType.DEPOSIT))

    const depositRemainingLimits = computed(() => getRemainingLimits(isDepositLimitEnabled.value ? LimitType.DEPOSIT : undefined))

    const depositRemainingLimit = computed(() => depositRemainingLimits.value?.[0])

    const hasReachedDepositLimit = (minAmount?: number) => depositRemainingLimits.value.some(limit =>
        minAmount
            ? Number(minAmount) > Number(limit.remaining)
            : Number(limit.remaining) <= 0
    )

    const isLossLimitEnabled = computed(() => enabledConfigs.value.some(limitConfig => limitConfig.type === LimitType.LOSS))

    function nearlyReachedLimitsForType(limitType: LimitTypeUnion) {
        if (limitType === LimitType.DEPOSIT) return []

        return getRemainingLimits(limitType).filter(limit => Number(limit.remaining) <= 3)
    }

    const nearlyReachedLimits = computed(() => type.value ? nearlyReachedLimitsForType(type.value) : [])

    const isValid = computed(() => {
        if (hasValidationError.value) return false
        if (!areLimitsUnderMax.value) return false
        if (!isAboveOrEqualToMinAmount.value) return false
        if (requirement.value === Requirement.SINGLE) return atLeastOneFilled.value

        return true
    })

    function reachedLimitsForType(limitType: LimitTypeUnion, minAmount?: number) {
        const minimumAmount = minAmount ?? 0

        return getRemainingLimits(limitType).filter((limit) => {
            if (limit.remaining === 'inf') return false
            if (limitType === LimitType.DEPOSIT && Number(minimumAmount) > Number(limit.remaining)) return true

            return Number(limit.remaining) === 0
        })
    }

    function reachedLimits(minAmount?: number) {
        if (!type.value) return []

        return reachedLimitsForType(type.value, minAmount)
    }

    const resetValidationError = (period: Period) => {
        validationErrors.value[period as Period] = null
    }

    function getEffectiveLimitValue(limit: Limit) {
        return limit.pendingValue ?? limit.value
    }

    function setCurrentLimits(preserveInputType = false) {
        if (!config.value) return
        if (!limits.value) return

        if (!config.value.userHasAnyLimit) {
            mutatingLimits.value[Period.DAILY].active = true

            return
        }

        Object.values(limits.value).forEach((limit: Limit) => {
            const effectiveValue = getEffectiveLimitValue(limit)

            if (!effectiveValue) return
            if (effectiveValue === 'inf') return

            mutatingLimits.value[limit.period].value = Number(effectiveValue as LimitValue)
            mutatingLimits.value[limit.period].active = true
        })

        if (!preserveInputType) {
            checkIfLimitExistsOnSlider()
        }
        else {
            isLoading.value = false
        }
    }

    const toggleActiveLimit = (period: Period) => {
        mutatingLimits.value[period].active = !mutatingLimits.value[period].active

        if (!mutatingLimits.value[period].active) {
            mutatingLimits.value[period].value = 'inf'
        }
    }

    const pendingMinError = shallowRef<{ period: Period, min: number } | null>(null)

    const { start: startMinErrorTimer, stop: stopMinErrorTimer } = useTimeoutFn(() => {
        if (!pendingMinError.value) return

        const { period, min } = pendingMinError.value
        validationErrors.value[period] = { message: t('payments.moneyLimits.minLimit', { amount: asMoney(min, { decimals: 0 }) }), type: 'error' }
    }, 800, { immediate: false })

    const resetValidationErrors = () => {
        stopMinErrorTimer()
        pendingMinError.value = null
        clampedValues.value = {}

        Object.keys(validationErrors.value).forEach(period => resetValidationError(period as Period))
    }

    function setLimit(event: Event, period: Period) {
        if (!sliderOptions.value) return

        alert.value = undefined
        stopMinErrorTimer()
        pendingMinError.value = null

        const amount = (event?.target as HTMLInputElement)?.value ?? event
        const maxSliderAmountForPeriod = Number(sliderOptions.value[period].max)
        const minSliderAmountForPeriod = Number(sliderOptions.value[period].min)

        if (!amount) {
            resetValidationError(period)
            mutatingLimits.value[period].value = amount

            return
        }

        if (Number(amount) > maxSliderAmountForPeriod) {
            mutatingLimits.value[period].value = maxSliderAmountForPeriod
            clampedValues.value[period] = maxSliderAmountForPeriod
            validationErrors.value[period] = { message: t('payments.moneyLimits.limitReachedAdjusted', { amount: asMoney(maxSliderAmountForPeriod, { decimals: 0 }) }), type: 'warning' }

            return
        }

        if (Number(amount) < minSliderAmountForPeriod) {
            mutatingLimits.value[period].value = amount
            resetValidationError(period)
            pendingMinError.value = { period, min: minSliderAmountForPeriod }
            startMinErrorTimer()

            return
        }

        mutatingLimits.value[period].value = amount

        // The mask re-emits the clamped value right after a clamp; don't let it clear the message
        if (clampedValues.value[period] !== undefined && Number(amount) === clampedValues.value[period]) return

        delete clampedValues.value[period]
        resetValidationError(period)
    }

    function getPendingMessageForPeriod(period: Period) {
        if (!type.value) return
        if (!limits.value) return

        const pendingLimit = getPendingLimit(period)

        if (!pendingLimit) return

        const pendingLimitTime = asRelativeTime(pendingLimit.pendingUntil, { timeFormat: 'long', length: 2 })
        const amount: string = pendingLimit.pendingValue ? asMoney(Number(pendingLimit.pendingValue), { decimals: 0 }) as string : ''
        const limitPeriodText = t(`payments.moneyLimits.${type.value}.your${firstToUpper(period)}Limit`)

        if (pendingLimit.pendingValue === 'inf') {
            return t(`payments.moneyLimits.${type.value}.pendingLimits.removalMessage`, {
                limitPeriodText: limitPeriodText,
                time: pendingLimitTime,
            })
        }

        return t(`payments.moneyLimits.${type.value}.pendingLimits.message`, {
            limitPeriodText: limitPeriodText,
            amount: amount,
            time: pendingLimitTime,
        })
    }

    const getReachedLimit = (period: Period) => reachedLimits()?.find(limit => limit.period === period)

    const getValidationError = (period: Period) => validationErrors.value[period as Period]

    const getPendingLimit = (period: Period) => limits.value?.[period]?.pendingValue
        ? limits.value?.[period]
        : undefined

    const hasReachedLimit = (minAmount?: number) => remainingLimits.value?.some(limit =>
        isDepositLimit.value && minAmount
            ? Number(minAmount) > Number(limit.remaining)
            : Number(limit.remaining) <= 0
    )

    async function refreshLimitData() {
        await Promise.all([configsQuery.refresh(), limitsQuery.refresh()])

        Object.values(limits.value).forEach((limit: Limit) => {
            const effectiveValue = getEffectiveLimitValue(limit)

            if (!effectiveValue) return
            mutatingLimits.value[limit.period as Period].value = effectiveValue === 'inf'
                ? 'inf'
                : Number(effectiveValue)
        })
    }

    function checkIfLimitExistsOnSlider() {
        if (!config.value?.userHasAnyLimit) {
            inputType.value = InputType.SLIDERS
            isLoading.value = false

            return
        }

        const isCurrentLimitInput = Object.values(limits.value).some((limit: Limit) => {
            const effectiveValue = getEffectiveLimitValue(limit)

            if (!effectiveValue) return false
            if (effectiveValue === 'inf') return false

            return !sliderOptions.value?.[limit.period].steps.includes(Number(effectiveValue))
        })

        inputType.value = isCurrentLimitInput ? InputType.INPUTS : InputType.SLIDERS
        isLoading.value = false
    }

    async function leaveLimits() {
        if (hasPendingLimits.value && wantsToEditLimits.value) {
            wantsToEditLimits.value = false

            return
        }

        if (showDialog.value) {
            showDialog.value = false
            isFromManualLink.value = false

            return
        }

        await navigateTo(t('pages.lobby'))
    }

    // A manual link is an explicit "adjust my limits" entry: with exactly one limit set the
    // type is a given, otherwise the user picks again instead of landing on a stale selection.
    function openLimitsDialogFromManualLink() {
        wantsToPickLimitType.value = false

        if (hasMultipleLimitTypes.value) {
            selectedLimitType.value = limitTypesWithLimits.value.length === 1
                ? limitTypesWithLimits.value[0]
                : undefined
        }
        isFromManualLink.value = true
        showDialog.value = true
    }

    // Entry points tied to a specific limit (a reached-limit alert's adjust button) must land
    // on that limit's type, not on the picker or a stale selection.
    function openLimitsDialogForType(limitType: LimitTypeUnion) {
        wantsToPickLimitType.value = false

        if (hasMultipleLimitTypes.value) selectLimitType(limitType)

        isFromManualLink.value = true
        showDialog.value = true
    }

    function afterUpdate() {
        hasAcceptedLimitsAfterReactivation.value = '1'
        showDialog.value = false
        updating.value = false
        wantsToEditLimits.value = false
        isFromManualLink.value = false
    }

    watch(showDialog, (isOpen) => {
        if (!isOpen) {
            wantsToEditLimits.value = false
            isPickingLimitType.value = false
            isExplanationPassed.value = false
        }
    })

    function getInputMaskForPeriod(period: Period) {
        if (!sliderOptions.value) return '#'.repeat(7)

        return '#'.repeat(sliderOptions.value[period].max.toString().length + 1)
    }

    // "Remove limit" is type-scoped: it clears the currently selected type's limits, unlike
    // handleNoAnswer which answers the prompt and may target a different (unanswered) type.
    function removeCurrentLimits() {
        if (requirement.value !== Requirement.OPTIONAL) return

        Object.values(mutatingLimits.value).forEach(mutatingLimit => mutatingLimit.value = 'inf')

        return updateLimits()
    }

    // Skipping answers only the loss type on multi-type sites: an inf deposit "limit" would
    // count as a set limit everywhere else in the app. Any set limit counts as having
    // answered the prompt, so the deposit type can stay untouched.
    async function handleNoAnswer() {
        const unansweredConfigs = enabledConfigs.value.filter(limitConfig => limitConfig.requirement === Requirement.OPTIONAL && !limitConfig.userHasAnyLimit)
        const skippedConfig = unansweredConfigs.find(limitConfig => limitConfig.type === LimitType.LOSS) ?? unansweredConfigs[0]

        if (!skippedConfig) return
        if (updating.value) return

        updating.value = true

        const infLimits = {
            [Period.DAILY]: 'inf',
            [Period.WEEKLY]: 'inf',
            [Period.MONTHLY]: 'inf',
        }

        try {
            await client.POST('/public/limits/{limitType}', {
                body: {
                    limits: infLimits,
                    brand: site.brand as MoneyLimitsComponents['schemas']['Brand'],
                },
                params: { path: { limitType: LIMIT_TYPE_TO_CLIENT[skippedConfig.type] } },
            })

            skippedConfig.userHasAnyLimit = true

            await refreshLimitData()

            afterUpdate()
        }
        catch (error) {
            updating.value = false

            alert.value = {
                type: 'danger',
                message: (OpenapiError.isError(error) ? error.cause?.message : undefined) || (error instanceof Error ? error.message : undefined) || t('core.defaultErrorMessage')
            }
        }
    }

    function useConfigs(options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/limits/config', {
                signal,
                retry: 3,
            })

            if (!data) throw data

            // Real key is `highRisks`; spec wrongly says `highRiskLimits` (fixed in money-limits!659) — type error until client regen.
            highRiskDepositLimitReached.value = data.highRisks?.depositLimitReached ?? false

            const hasEnabledConfig = Object.values(data).some(limitConfig => !!limitConfig && 'enabled' in limitConfig && limitConfig.enabled)

            if (!hasEnabledConfig) throw data

            return data
        }, {
            lazy: true,
            server: false,
            ...options,
            key: 'moneyLimits:configs',
        })
    }

    function useSliderOptions(options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const limitType = toValue(type)

            if (!limitType) return

            const { data } = await client.GET('/public/limits/{limitType}/sliders', {
                signal,
                retry: 3,
                params: { path: { limitType: LIMIT_TYPE_TO_CLIENT[limitType] } },
            })

            return data
        }, {
            lazy: true,
            server: false,
            ...options,
            enabled: () => {
                if (!toValue(type)) return false
                if (needsLimitTypeSelection.value) return false
                if (options.enabled === false) return false

                if (isRefOrGetter(options.enabled)) {
                    return toValue(options.enabled)
                }

                return options.enabled ?? true
            },
            key: 'moneyLimits:slider-options',
            queryCache: false,
        })
    }

    function useLimits(options: UseQueryOverrideOptions = {}) {
        return useQuery(async ({ signal }) => {
            const entries = await Promise.all(limitTypesWithLimits.value.map(async (limitType) => {
                const { data } = await client.GET('/public/limits/{limitType}', {
                    signal,
                    retry: 3,
                    params: { path: { limitType: LIMIT_TYPE_TO_CLIENT[limitType] } },
                })

                if (!data) throw data

                return [limitType, data] as const
            }))

            return Object.fromEntries(entries) as Partial<Record<LimitTypeUnion, LimitStatuses>>
        }, {
            lazy: true,
            server: false,
            ...options,
            enabled: () => {
                if (!limitTypesWithLimits.value.length) return false
                if (options.enabled === false) return false

                if (isRefOrGetter(options.enabled)) {
                    return toValue(options.enabled)
                }

                return options.enabled ?? true
            },
            key: 'moneyLimits:limits',
            queryCache: false,
        })
    }

    const configsQuery = useConfigs({ enabled: false })
    const sliderOptionsQuery = useSliderOptions({ enabled: false })
    const limitsQuery = useLimits({ enabled: false })

    // Switching type replaces the inputs wholesale: reset, then re-apply the new type's
    // persisted limits. The slider check runs here (not via setCurrentLimits) so it covers
    // the no-limit case too, and only after refresh so it compares against the new type's steps.
    async function selectLimitType(limitType: LimitTypeUnion) {
        selectedLimitType.value = limitType

        resetValidationErrors()
        mutatingLimits.value = createDefaultMutatingLimits()

        await sliderOptionsQuery.refresh()

        setCurrentLimits(true)
        checkIfLimitExistsOnSlider()
    }

    const permanentError = computed<UIAlertProps | undefined>(() => {
        const errorObject = [
            configsQuery.error,
            sliderOptionsQuery.error,
            limitsQuery.error,
        ]

        const requestError = errorObject.find(error => error.value !== undefined)

        if (!requestError) return

        // Narrow to the openapi error: the union also includes a plain `Error` with no `.data`.
        const error = requestError.value
        const message = OpenapiError.isError(error) ? error.data?.message : undefined

        return {
            type: 'danger',
            message: message || t('core.defaultErrorMessage')
        }
    })

    async function updateLimits() {
        const limitType = type.value

        if (!limitType) return
        if (updating.value) return

        updating.value = true

        const changedLimits = Object.values(mutatingLimits.value).reduce((acc: Record<string, string>, limit) => {
            acc[limit.period] = asEffectiveValue(limit.value)

            return acc
        }, {} as Record<string, string>)

        if (!Object.keys(changedLimits)?.length) {
            updating.value = false
            return
        }

        try {
            const { data } = await client.POST('/public/limits/{limitType}', {
                body: {
                    limits: changedLimits,
                    brand: site.brand as MoneyLimitsComponents['schemas']['Brand'],
                },
                params: { path: { limitType: LIMIT_TYPE_TO_CLIENT[limitType] } },
            })

            // The spec types this 200 as `SetLimitsResponse | string` (scramble anyOf artifact); narrow off the string branch.
            if (data && typeof data !== 'string' && data.limits) {
                config.value!.userHasAnyLimit = true
                // Before the refresh: the refreshed data must not reopen the reactivation dialog
                hasAcceptedLimitsAfterReactivation.value = '1'

                await refreshLimitData()

                afterUpdate()

                if (!hasPendingLimits.value) {
                    alert.value = {
                        type: 'success',
                        message: t('payments.moneyLimits.updated')
                    }
                }
            }
            else {
                afterUpdate()
            }

            return data
        }
        catch (error) {
            updating.value = false

            alert.value = {
                type: 'danger',
                message: (OpenapiError.isError(error) ? error.cause?.message : undefined) || (error instanceof Error ? error.message : undefined) || t('core.defaultErrorMessage')
            }

            return error
        }
    }

    async function confirmLimits() {
        const limitType = type.value

        if (!limitType) return
        if (updating.value) return

        updating.value = true

        try {
            const { data } = await client.POST('/public/limits/{limitType}/confirm', {
                // confirm takes no request body; the controller ignores brand.
                params: { path: { limitType: LIMIT_TYPE_TO_CLIENT[limitType] } },
            })

            // Before closing: until the refetch lands, the cached config would ask for this confirmation again.
            // By type, not config: the selection or the configs can change while the request is in flight.
            const confirmedConfig = enabledConfigs.value.find(limitConfig => limitConfig.type === limitType)

            if (confirmedConfig) confirmedConfig.canPendingBeConfirmed = false
            showDialog.value = false
            updating.value = false
            isFromManualLink.value = false

            configsQuery.refresh()
            limitsQuery.refresh()

            return data
        }
        catch (error) {
            updating.value = false
            alert.value = {
                type: 'danger',
                message: (OpenapiError.isError(error) ? error.cause?.message : undefined) || (error instanceof Error ? error.message : undefined) || t('core.defaultErrorMessage')
            }

            return error
        }
    }

    return configsQuery.bind({
        version,
        isVariantB,
        hasAcceptedLimitsAfterReactivation,
        adjustLimitsAfterReactivation,
        inputType,
        config,
        enabledConfigs,
        hasMultipleLimitTypes,
        needsLimitTypeSelection,
        limitTypesWithLimits,
        hasLimitsToFetch,
        isReactivatedUser,
        openLimitsDialogFromManualLink,
        selectedLimitType,
        selectLimitType,
        showDialog,
        isFromManualLink,
        wantsToEditLimits,
        isPickingLimitType,
        isExplanationPassed,
        wantsToPickLimitType,
        sliderOptions,
        limits,
        reactivatedLimits,
        limitsLoaded,
        pendingLimits,
        hasPendingLimits,
        hasChanges,
        employeeLimits,
        mutatingLimits,
        alert,
        permanentError,
        isLoading,
        updating,
        highRiskDepositLimitReached,
        type,
        prompt,
        requirement,
        requireMoneyLimits,
        atLeastOneFilled,
        isSignupPrompt,
        isPlayPrompt,
        isDepositLimit,
        isLossLimit,
        isValid,
        setCurrentLimits,
        toggleActiveLimit,
        setLimit,
        reachedLimits,
        reachedLimitsForType,
        nearlyReachedLimits,
        nearlyReachedLimitsForType,
        openLimitsDialogForType,
        activeLimits,
        inactiveLimits,
        remainingLimits,
        remainingLimit,
        isDepositLimitEnabled,
        isLossLimitEnabled,
        depositRemainingLimit,
        hasReachedDepositLimit,
        getValidationError,
        getPendingLimit,
        getReachedLimit,
        getPendingMessageForPeriod,
        getInputMaskForPeriod,
        resetValidationError,
        resetValidationErrors,
        checkIfLimitExistsOnSlider,
        hasReachedLimit,
        afterUpdate,
        refreshLimitData,
        useConfigs,
        useSliderOptions,
        useLimits,
        handleNoAnswer,
        removeCurrentLimits,
        leaveLimits,
        updateLimits,
        confirmLimits,
    })
}
